import React, { useState } from "react";
import { Box } from "@mui/material";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import { IconComponent } from "../../../components/Icon";
import { ICON_LABELS } from "../../../utils/constants/UI/iconLabels";
import { SHADOW } from "../../../utils/constants/UI";
import {
  formatDisplayText,
  formatWeight,
  formatConfidence,
  formatAnswer,
  cleanEvidenceText,
} from "../../../utils/helpers/formatDisplayText";
import {
  GREY,
  SUCCESS,
  ERROR,
  WARNING,
  HELP,
  BASE,
  BORDER,
  TEAL,
  ACTIONS,
  PURPLE,
} from "../../../utils/constants/colors";

/* ── Colour helpers ────────────────────────────────────────────────── */

const WEIGHT_COLORS = {
  dealbreaker: { bg: ERROR.W_50, text: ERROR.W_700, border: ERROR.W_200, dot: ERROR.W_500 },
  "must-have": { bg: SUCCESS.W_50, text: SUCCESS.W_700, border: SUCCESS.W_200, dot: SUCCESS.W_500 },
  must_have: { bg: SUCCESS.W_50, text: SUCCESS.W_700, border: SUCCESS.W_200, dot: SUCCESS.W_500 },
  "nice-to-have": { bg: WARNING.W_50, text: WARNING.W_700, border: WARNING.W_200, dot: WARNING.W_500 },
  nice_to_have: { bg: WARNING.W_50, text: WARNING.W_700, border: WARNING.W_200, dot: WARNING.W_500 },
};

const getWeightStyle = (w) => {
  const key = (w || "").toLowerCase().replace(/ /g, "_");
  if (WEIGHT_COLORS[key]) return WEIGHT_COLORS[key];
  if (key.includes("deal")) return WEIGHT_COLORS.dealbreaker;
  if (key.includes("must")) return WEIGHT_COLORS["must-have"];
  return WEIGHT_COLORS["nice-to-have"];
};

const getWeightLabel = (w) => formatWeight(w).toUpperCase();

const getScoreColor = (score, matched) => {
  if (matched || score >= 70) return { bg: SUCCESS.W_50, text: SUCCESS.W_700, border: SUCCESS.W_200, ring: SUCCESS.W_300 };
  if (score >= 40) return { bg: WARNING.W_50, text: WARNING.W_700, border: WARNING.W_200, ring: WARNING.W_300 };
  return { bg: ERROR.W_50, text: ERROR.W_700, border: ERROR.W_200, ring: ERROR.W_300 };
};

const getOverallScoreColor = (score) => {
  if (score >= 70) return { main: SUCCESS.W_500, bg: SUCCESS.W_50, text: SUCCESS.W_700, track: SUCCESS.W_100, glow: "rgba(1,166,62,0.08)" };
  if (score >= 40) return { main: WARNING.W_500, bg: WARNING.W_50, text: WARNING.W_700, track: WARNING.W_100, glow: "rgba(244,161,24,0.08)" };
  return { main: ERROR.W_500, bg: ERROR.W_50, text: ERROR.W_700, track: ERROR.W_100, glow: "rgba(231,63,63,0.08)" };
};

const getConfidenceColor = (level) => {
  const l = (level || "").toLowerCase();
  if (l === "high") return { bg: SUCCESS.W_50, text: SUCCESS.W_700 };
  if (l === "medium") return { bg: WARNING.W_50, text: WARNING.W_700 };
  return { bg: ERROR.W_50, text: ERROR.W_700 };
};

const HOOK_STRENGTH_COLORS = {
  strong: { bg: SUCCESS.W_50, text: SUCCESS.W_700, border: SUCCESS.W_200 },
  medium: { bg: WARNING.W_50, text: WARNING.W_700, border: WARNING.W_200 },
  weak: { bg: ERROR.W_50, text: ERROR.W_700, border: ERROR.W_200 },
};

const getSignalColor = (level) => {
  const l = (level || "").toLowerCase();
  if (l === "high" || l === "strong") return { bg: SUCCESS.W_50, text: SUCCESS.W_700, border: SUCCESS.W_200 };
  if (l === "medium") return { bg: WARNING.W_50, text: WARNING.W_700, border: WARNING.W_200 };
  return { bg: ERROR.W_50, text: ERROR.W_700, border: ERROR.W_200 };
};

/* ── URL / domain helpers ──────────────────────────────────────────── */

const extractDomain = (url) => {
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace("www.", "");
  } catch {
    return null;
  }
};

const getFaviconUrl = (domain) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;

/* ── Rich text (clickable links in evidence) ───────────────────────── */

const extractUrls = (text) => {
  if (!text) return [];
  const urlRegex = /https?:\/\/[^\s)}\]]+/g;
  const matches = text.match(urlRegex) || [];
  const parenRegex = /\(([a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s)]*)\)/g;
  let m;
  while ((m = parenRegex.exec(text)) !== null) {
    if (!m[1].startsWith("http")) matches.push(`https://${m[1]}`);
  }
  return [...new Set(matches)];
};

const RichText = ({ text }) => {
  if (!text) return null;
  const urls = extractUrls(text);
  if (!urls.length) {
    return (
      <span style={{ fontSize: "13px", color: GREY.SECONDARY, lineHeight: "1.7" }}>{text}</span>
    );
  }
  let remaining = text;
  const parts = [];
  urls.forEach((url) => {
    const idx = remaining.indexOf(url);
    if (idx === -1) {
      const raw = url.replace("https://", "");
      const rawIdx = remaining.indexOf(raw);
      if (rawIdx !== -1) {
        parts.push(remaining.slice(0, rawIdx));
        parts.push({ type: "link", url, raw });
        remaining = remaining.slice(rawIdx + raw.length);
      }
      return;
    }
    parts.push(remaining.slice(0, idx));
    parts.push({ type: "link", url, raw: url });
    remaining = remaining.slice(idx + url.length);
  });
  if (remaining) parts.push(remaining);

  return (
    <span style={{ fontSize: "13px", color: GREY.SECONDARY, lineHeight: "1.7" }}>
      {parts.map((part, i) => {
        if (typeof part === "string") return <span key={i}>{part}</span>;
        const domain = extractDomain(part.url);
        return (
          <a
            key={i}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: HELP.W_500,
              textDecoration: "none",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
          >
            {domain && (
              <img
                src={getFaviconUrl(domain)}
                alt=""
                style={{ width: 14, height: 14, borderRadius: 2, verticalAlign: "middle" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            )}
            {domain || part.raw}
            <IconComponent iconLabel={ICON_LABELS.OPEN_IN_NEW} color={HELP.W_500} fontSize="11px" />
          </a>
        );
      })}
    </span>
  );
};

/* ── Score ring (SVG arc) ──────────────────────────────────────────── */

const ScoreRing = ({ score, size = 120, strokeWidth = 8, color }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const sc = color || getOverallScoreColor(score);

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={sc.track} strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={sc.main}
          strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: "28px", fontWeight: 800, color: sc.main, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: "11px", fontWeight: 600, color: GREY.TERTIARY, marginTop: "2px" }}>/ 100</span>
      </div>
    </div>
  );
};

/* ── Stats badge row ───────────────────────────────────────────────── */

const StatBadge = ({ icon, label, value, color }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: "8px", padding: "8px 14px",
      background: BASE.WHITE, border: `1px solid ${BORDER.LIGHT}`, borderRadius: "10px",
      flex: 1, minWidth: 0,
    }}
  >
    <div style={{ width: 32, height: 32, borderRadius: "8px", background: color.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <IconComponent iconLabel={icon} color={color.main} fontSize="16px" />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "10px", fontWeight: 500, color: GREY.TERTIARY, letterSpacing: "0.3px", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: "14px", fontWeight: 700, color: GREY.PRIMARY, marginTop: "1px" }}>{value}</div>
    </div>
  </div>
);

/* ── Research block inside criteria card ───────────────────────────── */

const ResearchBlock = ({ research }) => {
  const SENTINELS = new Set(["no_evidence", "n/a", "none", "unknown", "not_found", "not_available"]);
  const isSentinelAnswer = !research.answer || SENTINELS.has((research.answer || "").toLowerCase().trim());
  const { cleanText, urls } = research.evidence ? cleanEvidenceText(research.evidence) : { cleanText: "", urls: [] };
  const hasRealEvidence = cleanText || urls.length > 0;
  if (isSentinelAnswer && !hasRealEvidence) return null;
  const displayAnswer = isSentinelAnswer ? null : formatAnswer(research.answer);

  return (
    <div style={{ marginTop: "12px", padding: "12px 14px", background: BASE.BACKGROUND_LIGHT, border: `1px solid ${BORDER.LIGHT}`, borderRadius: "8px" }}>
      {displayAnswer && (
        <div style={{ marginBottom: "6px", display: "flex", alignItems: "baseline", gap: "4px" }}>
          <span style={{ fontSize: "11px", fontWeight: 700, color: GREY.PRIMARY, textTransform: "uppercase", letterSpacing: "0.3px" }}>Answer:</span>
          <span style={{ fontSize: "13px", fontWeight: 600, color: GREY.PRIMARY }}>{displayAnswer}</span>
        </div>
      )}
      {cleanText && <span style={{ fontSize: "12px", color: GREY.SECONDARY, lineHeight: "1.6" }}>{cleanText}</span>}
      {urls.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
          {urls.map((u, i) => (
            <a key={i} href={u.url} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", background: BASE.WHITE, border: `1px solid ${BORDER.DEFAULT}`, borderRadius: "6px", fontSize: "11px", color: HELP.W_600, textDecoration: "none", fontWeight: 600, transition: "all 0.15s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = HELP.W_300; e.currentTarget.style.background = HELP.W_50; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER.DEFAULT; e.currentTarget.style.background = BASE.WHITE; }}
            >
              <img src={`https://www.google.com/s2/favicons?domain=${u.domain}&sz=16`} alt="" style={{ width: 13, height: 13, borderRadius: 2 }} onError={(e) => { e.target.style.display = "none"; }} />
              {u.domain}
              <IconComponent iconLabel={ICON_LABELS.OPEN_IN_NEW} color={HELP.W_400} fontSize="10px" />
            </a>
          ))}
        </div>
      )}
      {research.confidence && (() => {
        const cc = getConfidenceColor(research.confidence);
        return (
          <div style={{ marginTop: "8px" }}>
            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.4px", background: cc.bg, color: cc.text }}>
              {formatConfidence(research.confidence)} Confidence
            </span>
          </div>
        );
      })()}
    </div>
  );
};

/* ── Criteria card (expandable) ────────────────────────────────────── */

const CriteriaCard = ({ criterion, score, matched, weight, reason, research, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded || false);
  const ws = getWeightStyle(weight);
  const sc = getScoreColor(score, matched);
  const hasResearch = !!research;

  return (
    <div
      style={{ border: `1px solid ${BORDER.LIGHT}`, borderRadius: "12px", background: BASE.WHITE, overflow: "hidden", transition: "box-shadow 0.2s ease, border-color 0.2s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = SHADOW.sm; e.currentTarget.style.borderColor = BORDER.HOVER; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = BORDER.LIGHT; }}
    >
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", cursor: hasResearch ? "pointer" : "default", userSelect: "none" }}
        onClick={() => hasResearch && setExpanded(!expanded)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <div style={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: matched ? SUCCESS.W_50 : ERROR.W_50, border: `1.5px solid ${matched ? SUCCESS.W_200 : ERROR.W_200}`, flexShrink: 0 }}>
            <IconComponent iconLabel={matched ? ICON_LABELS.CHECK : ICON_LABELS.CLOSE} color={matched ? SUCCESS.W_500 : ERROR.W_500} fontSize="14px" />
          </div>
          <span style={{ fontSize: "14px", fontWeight: 600, color: GREY.PRIMARY }}>{formatDisplayText(criterion)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "6px", background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, fontSize: "12px", fontWeight: 800, lineHeight: "16px" }}>{score}%</span>
          {weight && (
            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "6px", background: ws.bg, color: ws.text, border: `1px solid ${ws.border}`, fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: "14px" }}>
              {getWeightLabel(weight)}
            </span>
          )}
          {hasResearch && (
            <div style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.2s ease", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>
              <IconComponent iconLabel={ICON_LABELS.EXPAND_MORE} color={GREY.TERTIARY} fontSize="18px" />
            </div>
          )}
        </div>
      </div>
      {reason && <div style={{ padding: "0 16px 14px 50px" }}><RichText text={reason} /></div>}
      {hasResearch && expanded && <div style={{ padding: "0 16px 14px 50px" }}><ResearchBlock research={research} /></div>}
    </div>
  );
};

/* ── Rejection section ─────────────────────────────────────────────── */

const RejectionSection = ({ reasons }) => (
  <div style={{ marginTop: "24px", padding: "20px", border: `1px solid ${ERROR.W_200}`, borderRadius: "12px", background: ERROR.W_50 }}>
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
      <div style={{ width: 28, height: 28, borderRadius: "8px", background: ERROR.W_100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <IconComponent iconLabel={ICON_LABELS.CANCEL} color={ERROR.W_500} fontSize="16px" />
      </div>
      <span style={{ fontSize: "14px", fontWeight: 700, color: ERROR.W_700 }}>Why This Contact Was Rejected</span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {reasons.map((reason, idx) => (
        <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "12px 14px", background: BASE.WHITE, border: `1px solid ${ERROR.W_200}`, borderRadius: "8px" }}>
          <div style={{ width: 20, height: 20, borderRadius: "50%", background: ERROR.W_50, border: `1px solid ${ERROR.W_200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
            <IconComponent iconLabel={ICON_LABELS.CLOSE} color={ERROR.W_500} fontSize="11px" />
          </div>
          <span style={{ fontSize: "13px", color: GREY.PRIMARY, lineHeight: "1.6" }}>{reason.message || reason}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ── Generic section block (icon + title + children) ───────────────── */

const SectionBlock = ({ icon, title, children, accent }) => (
  <div style={{ marginTop: "12px", padding: "16px 18px", background: BASE.WHITE, border: `1px solid ${BORDER.LIGHT}`, borderRadius: "10px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
      <div style={{ width: 26, height: 26, borderRadius: "6px", background: accent?.bg || HELP.W_50, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <IconComponent iconLabel={icon} color={accent?.main || HELP.W_500} fontSize="15px" />
      </div>
      <span style={{ fontSize: "13px", fontWeight: 700, color: GREY.PRIMARY }}>{title}</span>
    </div>
    {children}
  </div>
);

/* ── Outreach strategy section ─────────────────────────────────────── */

const OutreachStrategySection = ({ strategy }) => {
  if (!strategy) return null;

  const {
    hook,
    pain_hypothesis,
    lead_service,
    positioning_and_tone,
    avoid,
    likely_objections,
    discovery_questions,
    signal_strength,
    signal_justification,
  } = strategy;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <IconComponent iconLabel={ICON_LABELS.MANAGE_SEARCH} color={HELP.W_500} fontSize="18px" />
          <span style={{ fontSize: "14px", fontWeight: 700, color: GREY.PRIMARY }}>Outreach Strategy</span>
        </div>
        {signal_strength && (() => {
          const ss = getSignalColor(signal_strength);
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "999px", background: ss.bg, color: ss.text, border: `1px solid ${ss.border}`, fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Signal: {signal_strength}
            </span>
          );
        })()}
      </div>

      {hook?.value && (
        <SectionBlock icon={ICON_LABELS.INSERT_DRIVE_FILE} title="Hook" accent={{ main: PURPLE.W_500, bg: PURPLE.W_50 }}>
          <div style={{ marginBottom: hook.evidence ? "10px" : 0 }}>
            <RichText text={hook.value} />
          </div>
          {hook.hook_strength && (() => {
            const hs = HOOK_STRENGTH_COLORS[(hook.hook_strength || "").toLowerCase()] || HOOK_STRENGTH_COLORS.medium;
            return (
              <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: "4px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.4px", background: hs.bg, color: hs.text, border: `1px solid ${hs.border}`, textTransform: "uppercase", marginRight: "8px" }}>
                {hook.hook_strength} hook
              </span>
            );
          })()}
          {hook.evidence && (
            <div style={{ marginTop: "10px", padding: "10px 12px", background: BASE.BACKGROUND_LIGHT, borderRadius: "6px", border: `1px solid ${BORDER.LIGHT}` }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.3px", marginRight: "6px" }}>Evidence:</span>
              <RichText text={hook.evidence} />
            </div>
          )}
        </SectionBlock>
      )}

      {pain_hypothesis?.value && (
        <SectionBlock icon={ICON_LABELS.CANCEL} title="Pain Hypothesis" accent={{ main: ERROR.W_500, bg: ERROR.W_50 }}>
          <div style={{ marginBottom: pain_hypothesis.evidence ? "10px" : 0 }}>
            <RichText text={pain_hypothesis.value} />
          </div>
          {pain_hypothesis.evidence && (
            <div style={{ marginTop: "8px", padding: "10px 12px", background: BASE.BACKGROUND_LIGHT, borderRadius: "6px", border: `1px solid ${BORDER.LIGHT}` }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.3px", marginRight: "6px" }}>Evidence:</span>
              <RichText text={pain_hypothesis.evidence} />
            </div>
          )}
        </SectionBlock>
      )}

      {(lead_service || positioning_and_tone) && (
        <SectionBlock icon={ICON_LABELS.BUSINESS} title="Positioning & Tone" accent={{ main: TEAL.W_500, bg: TEAL.W_100 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: positioning_and_tone?.rationale ? "10px" : 0 }}>
            {lead_service && (
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>Lead Service</div>
                <span style={{ fontSize: "13px", fontWeight: 600, color: GREY.PRIMARY }}>{formatDisplayText(lead_service)}</span>
              </div>
            )}
            {positioning_and_tone?.positioning && (
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>Positioning</div>
                <span style={{ fontSize: "13px", fontWeight: 600, color: GREY.PRIMARY }}>{formatDisplayText(positioning_and_tone.positioning)}</span>
              </div>
            )}
            {positioning_and_tone?.tone && (
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>Tone</div>
                <span style={{ fontSize: "13px", color: GREY.SECONDARY }}>{positioning_and_tone.tone}</span>
              </div>
            )}
          </div>
          {positioning_and_tone?.rationale && (
            <div style={{ padding: "10px 12px", background: BASE.BACKGROUND_LIGHT, borderRadius: "6px", border: `1px solid ${BORDER.LIGHT}` }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.3px", marginRight: "6px" }}>Rationale:</span>
              <RichText text={positioning_and_tone.rationale} />
            </div>
          )}
        </SectionBlock>
      )}

      {Array.isArray(avoid) && avoid.length > 0 && (
        <SectionBlock icon={ICON_LABELS.CLOSE} title="Angles to Avoid" accent={{ main: ERROR.W_500, bg: ERROR.W_50 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {avoid.map((item, i) => (
              <div key={i} style={{ padding: "10px 12px", border: `1px solid ${ERROR.W_200}`, borderLeft: `3px solid ${ERROR.W_500}`, borderRadius: "6px", background: ERROR.W_50 }}>
                {item.angle && <div style={{ fontSize: "13px", fontWeight: 700, color: GREY.PRIMARY, marginBottom: "4px" }}>{item.angle}</div>}
                {item.reason && <div style={{ marginBottom: item.evidence ? "6px" : 0 }}><RichText text={item.reason} /></div>}
                {item.evidence && (
                  <div style={{ marginTop: "6px" }}>
                    <span style={{ fontSize: "10px", fontWeight: 700, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.3px", marginRight: "6px" }}>Evidence:</span>
                    <RichText text={item.evidence} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {Array.isArray(likely_objections) && likely_objections.length > 0 && (
        <SectionBlock icon={ICON_LABELS.INFO} title="Likely Objections" accent={{ main: WARNING.W_500, bg: WARNING.W_50 }}>
          <ul style={{ margin: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "6px" }}>
            {likely_objections.map((obj, i) => (
              <li key={i} style={{ fontSize: "13px", color: GREY.SECONDARY, lineHeight: "1.6" }}>{obj}</li>
            ))}
          </ul>
        </SectionBlock>
      )}

      {Array.isArray(discovery_questions) && discovery_questions.length > 0 && (
        <SectionBlock icon={ICON_LABELS.FACT_CHECK} title="Discovery Questions" accent={{ main: HELP.W_500, bg: HELP.W_50 }}>
          <ol style={{ margin: 0, paddingLeft: "22px", display: "flex", flexDirection: "column", gap: "8px" }}>
            {discovery_questions.map((q, i) => (
              <li key={i} style={{ fontSize: "13px", color: GREY.PRIMARY, lineHeight: "1.6" }}>{q}</li>
            ))}
          </ol>
        </SectionBlock>
      )}

      {signal_justification && (
        <SectionBlock icon={ICON_LABELS.TRENDING_UP} title="Signal Justification" accent={{ main: PURPLE.W_500, bg: PURPLE.W_50 }}>
          <RichText text={signal_justification} />
        </SectionBlock>
      )}
    </div>
  );
};

/* ── Main dialog ───────────────────────────────────────────────────── */

const ICPAnalysisDialog = ({ open, onClose, contact }) => {
  if (!contact) return null;

  const skipReasons = contact._skipReasons || [];
  const isRejected = skipReasons.length > 0;
  const score = contact.company_score || 0;
  const sc = getOverallScoreColor(score);
  const criteria = contact.company_criteria_results || [];
  const totalCriteria = criteria.length;
  const matchedCount = criteria.filter((c) => c.matched).length;

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      close={onClose}
      width="64vw"
      header={{
        heading: {
          id: "icp-analysis",
          label: `${contact.name || "Contact"} - ICP Analysis`,
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          bgcolor: BASE.BACKGROUND_LIGHT,
          "&::-webkit-scrollbar": { width: "5px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: GREY.DISABLED, borderRadius: "3px" },
          "&::-webkit-scrollbar-thumb:hover": { background: GREY.TERTIARY },
        }}
      >
        {/* ─── Hero section: Score ring + summary + stats ─── */}
        <div
          style={{
            padding: "28px 32px 24px",
            background: `linear-gradient(135deg, ${BASE.WHITE} 0%, ${sc.glow} 100%)`,
            borderBottom: `1px solid ${BORDER.LIGHT}`,
          }}
        >
          <div style={{ display: "flex", gap: "28px", alignItems: "flex-start" }}>
            <div style={{ flexShrink: 0 }}>
              <ScoreRing score={score} size={120} strokeWidth={8} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <IconComponent iconLabel={ICON_LABELS.BUSINESS} color={HELP.W_500} fontSize="18px" />
                <span style={{ fontSize: "11px", fontWeight: 700, color: HELP.W_600, textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Company Analysis
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "12px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: GREY.PRIMARY }}>Overall Match Score</span>
                <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: "12px", background: sc.bg, color: sc.text, fontSize: "12px", fontWeight: 700 }}>
                  {score >= 70 ? "Strong Match" : score >= 40 ? "Partial Match" : "Weak Match"}
                </span>
              </div>
              {contact.company_summary && (
                <div style={{ padding: "12px 16px", background: BASE.WHITE, border: `1px solid ${BORDER.LIGHT}`, borderRadius: "10px", marginBottom: "14px" }}>
                  <span style={{ fontSize: "13px", color: GREY.PRIMARY, lineHeight: "1.7" }}>{contact.company_summary}</span>
                </div>
              )}
              <div style={{ display: "flex", gap: "10px" }}>
                <StatBadge icon={ICON_LABELS.FACT_CHECK} label="Criteria Evaluated" value={totalCriteria} color={{ main: HELP.W_500, bg: HELP.W_50 }} />
                <StatBadge icon={ICON_LABELS.CHECK_CIRCLE} label="Criteria Matched" value={`${matchedCount} / ${totalCriteria}`} color={{ main: SUCCESS.W_500, bg: SUCCESS.W_50 }} />
              </div>
            </div>
          </div>
        </div>

        {/* ─── Outreach strategy, rejection, criteria ─── */}
        <div style={{ padding: "24px 32px 32px" }}>
          {contact.outreach_strategy && (
            <div style={{ marginBottom: "28px" }}>
              <OutreachStrategySection strategy={contact.outreach_strategy} />
            </div>
          )}

          {isRejected && (
            <div style={{ marginBottom: "16px" }}>
              <RejectionSection reasons={skipReasons} />
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "14px", fontWeight: 700, color: GREY.PRIMARY }}>Evaluation Criteria</span>
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "1px 8px", borderRadius: "10px", background: ACTIONS.DEFAULT, fontSize: "11px", fontWeight: 700, color: GREY.SECONDARY }}>
                {totalCriteria}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: SUCCESS.W_500 }} />
                <span style={{ fontSize: "11px", color: GREY.TERTIARY }}>Matched</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: ERROR.W_500 }} />
                <span style={{ fontSize: "11px", color: GREY.TERTIARY }}>Not Matched</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {criteria.map((c, idx) => (
              <CriteriaCard key={idx} {...c} defaultExpanded={idx === 0} />
            ))}
          </div>
        </div>
      </Box>
    </CustomDialog>
  );
};

export default ICPAnalysisDialog;
